import { Close, MailOutline } from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { SHOP_CREATED, SHOP_IMPORTED } from '../../config/dataService/constants';
import { inputFormatPhoneNumber } from '../../container/Common/FormatPhoneNumber';
import { SmallPhoneTypeIcon } from '../../container/Customers/Profile/CustomerProfileLeftDataSection';
import { CHIP_COLOR } from '../../utility/Constant';
import FleetIconSvg from '../../utility/Svg/FleetIconSvg';
export const MultiSelectAutocomplete = ({
    selectedArray,
    setSelectedCustomer,
    search,
    setSearch,
    loading,
    isFetchingNextPage,
    optionsWithLoader,
    LOADER_OPTION,
    handleScroll,
    toast,
    /** Called when the dropdown closes; `hasSelection` is whether at least one customer is selected at close time */
    onDropdownClose,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const prevDropdownOpenRef = useRef(false);
    const [visibleCount, setVisibleCount] = useState(0);
    const containerRef = useRef(null);
    const chipsAreaRef = useRef(null);
    const measureContainerRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target) && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (prevDropdownOpenRef.current && !dropdownOpen) {
            onDropdownClose?.(selectedArray.length > 0);
        }
        prevDropdownOpenRef.current = dropdownOpen;
    }, [dropdownOpen, selectedArray.length, onDropdownClose]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (dropdownOpen && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
        if (!dropdownOpen) {
            setSearch('');
        }
    }, [dropdownOpen]);

    const othersPillSx = {
        flexShrink: 0,
        height: '28px',
        borderRadius: '8px',
        border: '1px solid #1976d2',
        background: '#E3F2FD',
        fontSize: '13px',
        fontWeight: 500,
        color: '#1976d2',
        cursor: 'pointer',
        '& .MuiChip-label': { padding: '0 6px' },
    };

    const GAP_PX = 4;

    const computeVisibleChipCount = useCallback(() => {
        const areaEl = chipsAreaRef.current;
        const measureRoot = measureContainerRef.current;
        if (!areaEl || !measureRoot || selectedArray.length === 0) {
            return selectedArray.length;
        }
        const chipRow = measureRoot.querySelector('[data-measure-chip-row]');
        const pillRow = measureRoot.querySelector('[data-measure-pill-row]');
        if (!chipRow?.children?.length) {
            return selectedArray.length;
        }
        const available = areaEl.clientWidth;
        const chipWidths = Array.from(chipRow.children).map((el) => el.getBoundingClientRect().width);
        const pillWidths = pillRow ? Array.from(pillRow.children).map((el) => el.getBoundingClientRect().width) : [];
        let best = 0;
        for (let i = 0; i < selectedArray.length; i++) {
            const count = i + 1;
            const hidden = selectedArray.length - count;
            let rowW = 0;
            for (let j = 0; j < count; j++) {
                rowW += chipWidths[j] + (j > 0 ? GAP_PX : 0);
            }
            if (hidden > 0) {
                const pillW = pillWidths[hidden - 1] ?? pillWidths[pillWidths.length - 1] ?? 0;
                rowW += GAP_PX + pillW;
            }
            if (rowW <= available + 0.5) {
                best = count;
            } else {
                break;
            }
        }
        return Math.max(1, best);
    }, [selectedArray]);

    useLayoutEffect(() => {
        if (dropdownOpen || selectedArray.length === 0) {
            setVisibleCount(selectedArray.length);
            return;
        }
        setVisibleCount(computeVisibleChipCount());
    }, [dropdownOpen, selectedArray, computeVisibleChipCount]);

    useEffect(() => {
        const areaEl = chipsAreaRef.current;
        if (!areaEl || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => {
            if (!dropdownOpen && selectedArray.length > 0) {
                setVisibleCount(computeVisibleChipCount());
            }
        });
        ro.observe(areaEl);
        return () => ro.disconnect();
    }, [dropdownOpen, selectedArray, computeVisibleChipCount]);

    // Dropdown position: calculated from containerRef
    const [dropdownStyle, setDropdownStyle] = useState({});
    useEffect(() => {
        if (dropdownOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 0,
                left: rect.left,
                width: rect.width,
                zIndex: 9999,
            });
        }
    }, [dropdownOpen, selectedArray]);

    const hidden = !dropdownOpen ? selectedArray.length - visibleCount : 0;

    const FilledTriangle = ({ up = false }) => (
        <svg width="10" height="7" viewBox="0 0 10 7" fill="none" style={{ transform: up ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
            <path d="M5 7L0 0H10L5 7Z" fill="#555" />
        </svg>
    );

    const chipSx = {
        flexShrink: 0,
        height: '30px',
        borderRadius: '8px',
        border: '1px solid #DEDEEF',
        background: '#F5F7FE',
        fontSize: '13px',
        fontWeight: 400,
        color: '#212121',
        '& .MuiChip-label': { padding: '5px 8px', paddingRight: '0!important', color: '#212121', fontSize: '14px' },
        '& .MuiChip-deleteIcon': { margin: '0 0 0 2px', color: '#757575' },
    };

    return (
        <Box sx={{ mb: 2.5, position: 'relative' }}>
            {/* Input Box */}
            <Box
                ref={containerRef}
                onClick={() => setDropdownOpen(true)}
                sx={{
                    border: '1px solid rgba(0,0,0,0.10)!important',
                    borderRadius: '8px',
                    background: '#fff',
                    boxShadow: '0 2px 3px 0 rgba(0,0,0,0.06)',
                    height: dropdownOpen ? 'auto' : '50px',
                    minHeight: '50px',
                    display: 'flex',
                    flexWrap: dropdownOpen ? 'wrap' : 'nowrap',
                    alignItems: dropdownOpen ? 'flex-start' : 'center',
                    // gap: '12px',
                    gap: '4px',
                    px: 1.5,
                    py: dropdownOpen ? '8px' : 0,
                    overflow: 'hidden',
                    cursor: 'text',
                    position: 'relative',
                    pr: dropdownOpen ? '36px' : 1.5,
                }}
            >
                {/* CLOSED STATE */}
                {!dropdownOpen && (
                    <>
                        {selectedArray.length === 0 && <Typography sx={{ fontSize: '15px', color: '#9E9E9E', flex: 1 }}>Select</Typography>}
                        {selectedArray.length > 0 && (
                            <Box
                                ref={chipsAreaRef}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: `${GAP_PX}px`,
                                    overflow: 'hidden',
                                }}
                            >
                                {selectedArray.slice(0, visibleCount).map((option) => (
                                    <Chip
                                        key={option.id}
                                        label={option.name}
                                        onDelete={(e) => {
                                            e.stopPropagation();
                                            setSelectedCustomer(selectedArray.filter((s) => s.id !== option.id));
                                        }}
                                        deleteIcon={<Close sx={{ fontSize: 16, width: '16px', height: '16px' }} style={{ marginRight: '8px' }} />}
                                        sx={chipSx}
                                    />
                                ))}
                                {hidden > 0 && (
                                    <Chip
                                        label={`+${hidden} ${hidden === 1 ? 'Other' : 'Others'}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDropdownOpen(true);
                                        }}
                                        sx={othersPillSx}
                                    />
                                )}
                            </Box>
                        )}
                        <Box sx={{ marginLeft: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', pl: 1 }}>
                            <FilledTriangle up={false} />
                        </Box>
                    </>
                )}

                {/* OPEN STATE */}
                {dropdownOpen && (
                    <>
                        {selectedArray.map((option) => (
                            <Chip
                                key={option.id}
                                label={option.name}
                                onDelete={(e) => {
                                    e.stopPropagation();
                                    setSelectedCustomer(selectedArray.filter((s) => s.id !== option.id));
                                }}
                                deleteIcon={<Close sx={{ fontSize: 16, width: '16px', height: '16px' }} style={{ marginRight: '8px' }} />}
                                sx={chipSx}
                            />
                        ))}
                        <input
                            ref={searchInputRef}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                border: 'none',
                                outline: 'none',
                                fontSize: '16PX',
                                minWidth: '120px',
                                flex: 1,
                                background: 'transparent',
                                color: '#212121',
                                padding: '2px 0',
                                alignSelf: 'center',
                            }}
                        />
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                setDropdownOpen(false);
                            }}
                            sx={{
                                position: 'absolute',
                                top: '16px',
                                right: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <FilledTriangle up={true} />
                        </Box>
                    </>
                )}
            </Box>

            {!dropdownOpen && selectedArray.length > 0 && (
                <Box
                    ref={measureContainerRef}
                    aria-hidden
                    sx={{
                        position: 'fixed',
                        left: -10000,
                        top: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                        visibility: 'hidden',
                        pointerEvents: 'none',
                        zIndex: -1,
                    }}
                >
                    <Box data-measure-chip-row sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: `${GAP_PX}px` }}>
                        {selectedArray.map((option) => (
                            <Chip
                                key={option.id}
                                label={option.name}
                                onDelete={() => {}}
                                deleteIcon={<Close sx={{ fontSize: 16, width: '16px', height: '16px' }} style={{ marginRight: '8px' }} />}
                                sx={chipSx}
                                tabIndex={-1}
                            />
                        ))}
                    </Box>
                    <Box data-measure-pill-row sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: `${GAP_PX}px` }}>
                        {[1, 2, 3, 4].map((n) => (
                            <Chip key={`measure-pill-${n}`} label={`+${n} ${n === 1 ? 'Other' : 'Others'}`} sx={othersPillSx} tabIndex={-1} />
                        ))}
                    </Box>
                </Box>
            )}

            {/* DROPDOWN — rendered via portal to escape modal overflow */}
            {dropdownOpen &&
                typeof document !== 'undefined' &&
                ReactDOM.createPortal(
                    <Box
                        ref={dropdownRef}
                        sx={{
                            ...dropdownStyle,
                            borderRadius: '8px',
                            background: '#fff',
                            boxShadow: '0 0 24px 0 rgba(0,0,0,0.10)!important',
                            border: '1px solid rgba(0,0,0,0.10)!important',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Options */}
                        <Box
                            onScroll={handleScroll}
                            sx={{
                                maxHeight: '300px',
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '6px' },
                            }}
                        >
                            {loading && search.trim().length >= 2 ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={22} />
                                </Box>
                            ) : optionsWithLoader.length === 0 ? (
                                <Box sx={{ py: 2, px: 2 }}>
                                    <Typography sx={{ color: '#9E9E9E', fontSize: '14px' }}>No customers found</Typography>
                                </Box>
                            ) : (
                                optionsWithLoader.map((option, idx) => {
                                    if (option?.id === LOADER_OPTION.id) {
                                        return (
                                            <Box key="loader" sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                                                <CircularProgress size={18} />
                                            </Box>
                                        );
                                    }
                                    const selected = selectedArray.some((s) => s.id === option.id);
                                    return (
                                        <Box
                                            key={option.id ?? idx}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (selected) {
                                                    setSelectedCustomer(selectedArray.filter((s) => s.id !== option.id));
                                                } else {
                                                    if (selectedArray.length >= 5) {
                                                        toast.error('Limit reached — you can select up to 5 customers per request.');
                                                        return;
                                                    }
                                                    setSelectedCustomer([...selectedArray, option]);
                                                    setSearch('');
                                                    setTimeout(() => searchInputRef.current?.focus(), 0);
                                                }
                                            }}
                                            sx={{
                                                display: 'flex',
                                                gap: '16px',
                                                alignItems: 'center',
                                                px: '12px',
                                                py: '8px',
                                                cursor: 'pointer',
                                                background: selected ? '#DEE9FB' : '#fff',
                                                '&:hover': { background: selected ? '#E3EDFF' : '#F9FAFB' },
                                            }}
                                        >
                                            {/* Checkbox */}
                                            <Box
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '4px',
                                                    border: selected ? '2px solid #006CDD' : '2px solid rgba(0, 0, 0, 0.10)',
                                                    background: selected ? '#006CDD' : '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    transition: 'all 0.15s ease',
                                                    boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)!important',
                                                }}
                                            >
                                                {selected && (
                                                    <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                                                        <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </Box>

                                            {/* Info */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    fontWeight={600}
                                                    fontSize={16}
                                                    sx={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        marginBottom: '6px',
                                                        color: '#212121',
                                                    }}
                                                >
                                                    {option.name}
                                                </Typography>
                                                {option.phoneNumber && (
                                                    <Typography
                                                        lineHeight={'150%'}
                                                        letterSpacing={'0.15px'}
                                                        fontSize={14}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            color: 'rgba(0, 0, 0, 0.80)',
                                                            fontWeight: 400,
                                                            mb: '8px',
                                                        }}
                                                    >
                                                        {SmallPhoneTypeIcon(option?.primaryPhoneNumberType || 'mobile')}
                                                        {inputFormatPhoneNumber(option.phoneNumber)}
                                                    </Typography>
                                                )}
                                                {option.email && (
                                                    <Typography
                                                        lineHeight={'150%'}
                                                        letterSpacing={'0.15px'}
                                                        fontSize={14}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            color: 'rgba(0, 0, 0, 0.80)',
                                                            fontWeight: 400,
                                                        }}
                                                    >
                                                        <MailOutline sx={{ fontSize: 16, width: '16px', height: '16px', color: '#1976D2' }} />
                                                        {option.email}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Right */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pl: 1, flexShrink: 0 }}>
                                                {option.isFleetCustomer && <FleetIconSvg height="26px" width="38px" />}
                                                <Chip
                                                    label={
                                                        option.createdFrom === SHOP_CREATED
                                                            ? 'Shop Created'
                                                            : option.createdFrom === SHOP_IMPORTED
                                                              ? 'Shop Imported'
                                                              : option.createdFrom === 'shop_guest'
                                                                ? 'Guest'
                                                                : 'Apex Auto'
                                                    }
                                                    sx={{
                                                        borderRadius: '100px',
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        height: '30px',
                                                        px: '8px',
                                                        backgroundColor:
                                                            option.createdFrom === SHOP_CREATED
                                                                ? CHIP_COLOR.SHOP_CREATED_BG
                                                                : option.createdFrom === SHOP_IMPORTED
                                                                  ? CHIP_COLOR.SHOP_IMPORTED_BG
                                                                  : option.createdFrom === 'shop_guest'
                                                                    ? CHIP_COLOR.SHOP_IMPORTED_BG
                                                                    : CHIP_COLOR.APEX_AUTO_APP_BG,
                                                        color:
                                                            option.createdFrom === SHOP_CREATED
                                                                ? CHIP_COLOR.SHOP_CREATED_COLOR
                                                                : option.createdFrom === SHOP_IMPORTED
                                                                  ? CHIP_COLOR.SHOP_IMPORTED_COLOR
                                                                  : option.createdFrom === 'shop_guest'
                                                                    ? CHIP_COLOR.SHOP_IMPORTED_COLOR
                                                                    : CHIP_COLOR.APEX_AUTO_APP_COLOR,
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>

                        {/* Footer */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: '12px',
                                py: '12px',
                                borderTop: '1px solid #F0F0F0',
                                background: '#F5F5F7',
                            }}
                        >
                            <Typography sx={{ fontSize: '14px', color: '#000', fontWeight: 400, lineHeight: '150%', letterSpacing: '0.15px' }}>
                                {selectedArray.length > 0 ? (
                                    <>
                                        <span style={{ color: '#006CDD' }}>{selectedArray.length} of 5</span>
                                        {` Customer${selectedArray.length > 1 ? 's' : ''} selected`}
                                    </>
                                ) : (
                                    'No customers selected'
                                )}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {selectedArray.length > 0 && (
                                    <Button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedCustomer([]);
                                        }}
                                        sx={{
                                            fontSize: '13px',
                                            fontWeight: 500,
                                            lineHeight: '22px',
                                            letterSpacing: '0.46px',
                                            p: '0!important',
                                            mr: '16px',
                                            ':hover': { background: 'transparent!important' },
                                        }}
                                    >
                                        CLEAR ALL
                                    </Button>
                                )}
                                <Button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDropdownOpen(false);
                                    }}
                                    variant="contained"
                                    sx={{
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        lineHeight: '22px',
                                        letterSpacing: '0.46px',
                                    }}
                                >
                                    DONE
                                </Button>
                            </Box>
                        </Box>
                    </Box>,
                    document.body
                )}
        </Box>
    );
};
